import { colorConstants } from 'config/colorConstants';
import dayjs, { Dayjs } from 'dayjs';
import { EditOutlined, PlusSquareOutlined, DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import {
    DimensionConfigType,
    EventViewType,
    KpiConfigType,
    PromoConfigDictionary,
    eventStatusType,
} from 'types/tradePromoOptimization';
import _, { isNil } from 'lodash';
import {
    arrayToString,
    formatDatePreservingTimeValues,
    formatNumWithKMB,
    formatNumWithKMBAlt,
    formatNumbers,
    getPercentageChangeV2,
} from 'utils/helperMethods';

export function parseCalenderData(
    calenderResponse: any,
    kpiConfigData: KpiConfigType | null,
    tpoCalenderLegendMapping: any,
    previousLegend: any,
    appConfig: any,
) {
    const { commonConfig, tradePromoOptimization } = appConfig;
    const { calenderHoverOptions, calendarColumnDropdownList } = tradePromoOptimization;

    // console.log(dimensionDropdownList, 'logging dimension dropdown list');

    // Always include all possible columns - filtering will happen in EventsTableContainer
    // This ensures all data is available for frontend filtering
    const nonCalenderColumns = calendarColumnDropdownList;

    const { decimalForPercentage, decimalForNonPercentage } = commonConfig;
    const {
        startWeeks,
        endWeeks,
        calenderData,
        approval_status,
        approval_request_ts,
        approval_log,
        publish_status,
        publish_time,
        publish_log,
    } = calenderResponse;

    // Select every 4th week starting with the first week
    const selectedWeeks = startWeeks.filter((data: any, index: number) => index % 4 === 0);

    const calenderColumns = selectedWeeks?.map((week: any) => {
        const formattedWeek = dayjs(week).format('MM/DD/YY');
        return {
            key: formattedWeek,
            label: formattedWeek,
        };
    });

    const result: any[] = [];
    let hoverData: any[] = calenderHoverOptions?.map((item: any) => {
        return {
            ...item,
            dataKey: item?.key,
        };
    });
    const legendObj: any = { ...previousLegend };
    const statues = {
        approval_status,
        approval_request_ts,
        approval_log,
        publish_status,
        publish_time,
        publish_log,
    };
    let deletedCountParser = 0;
    calenderData?.forEach((eventItem: any, index: number) => {
        // Create the output object with empty arrays as values
        const outputObj = selectedWeeks.reduce((acc: any, week: any) => {
            const formattedWeek = dayjs(week).format('MM/DD/YY');
            acc[formattedWeek] = [];
            return acc;
        }, {});
        const isEventDeleted =
            ['deleted']?.includes(eventItem?.promoStatus) ||
            (eventItem?.eventStatus === 'deleted' && !eventItem?.promoStatus);
        //maps the color,icon etc depending on whether its add,edit,delete
        const operationProperties = fetchTypeProperties(isEventDeleted ? 'deleted' : eventItem?.promoStatus);

        //increment deleted count
        if (isEventDeleted) deletedCountParser += 1;

        outputObj['operation'] = {
            type: isEventDeleted ? 'deleted' : eventItem?.promoStatus,
            label: operationProperties?.label,
            icon: operationProperties?.component,
            backgroundColor: operationProperties?.backgroundColor,
        };

        outputObj['event'] = eventItem?.event;
        outputObj['eventId'] = eventItem?.eventId;
        outputObj['key'] = eventItem?.event + eventItem?.eventId + eventItem?.promo + index;
        outputObj['keepDates'] = eventItem?.keep_dates;
        outputObj['keepPromo'] = eventItem?.keep_promo;
        outputObj['promoStatus'] = eventItem?.new_status;
        outputObj['planId'] = eventItem?.tpo_promo_id;
        outputObj['promotion_type_id'] = eventItem?.promotion_type_id;
        outputObj['tactic_type_id'] = eventItem?.tactic_type_id;
        outputObj['promotion_mechanic'] = eventItem?.promotion_mechanic;
        outputObj['tpm_tpo_id'] = eventItem?.tpm_tpo_id;
        outputObj['publish_status'] = eventItem?.publish_status;
        outputObj['publish_error'] = eventItem?.publish_error;
        outputObj['publish_time'] = eventItem?.publish_time;
        outputObj['is_publishable'] = eventItem?.is_publishable;
        outputObj['is_ever_successfully_published'] = eventItem?.is_ever_successfully_published;

        outputObj['tpm_promo_id'] = eventItem?.tpm_promo_id;
        if (eventItem?.startDate) {
            const { percentage: startDatePercentage, weekString } = calculatePercentage(
                eventItem?.startDate,
                outputObj,
            );
            const duration = calculateDifferenceInDays(dayjs(eventItem?.endDate), dayjs(eventItem?.startDate));
            const durationInPercentage = ((duration / 28) * 100).toFixed(2); //percentage of duration w.r.t month

            if (outputObj[weekString]) {
                const promoMappedObj = {
                    label: tpoCalenderLegendMapping?.[eventItem?.promotion_type_id]?.label ?? eventItem?.promo,
                    color: tpoCalenderLegendMapping?.[eventItem?.promotion_type_id]?.color ?? colorConstants.brown,
                };
                //promo col
                outputObj['promo'] = eventItem?.promo;
                outputObj['eventPromoId'] = eventItem?.event_promo_id;
                legendObj[promoMappedObj?.label] = { ...promoMappedObj };
                const infoData: any = {};
                calenderHoverOptions?.forEach((item: any) => {
                    const pre = item?.pre ?? '';
                    const post = item?.post ?? '';
                    let value = eventItem?.hoverData?.[item?.key];
                    //for numbers
                    if (!isNaN(value)) {
                        value = formatNumWithKMBAlt(
                            value,
                            post === '%' ? decimalForPercentage : decimalForNonPercentage,
                            post === '%' ? undefined : decimalForNonPercentage,
                        );
                    }
                    //for arrays
                    else if (Array.isArray(value)) {
                        value = arrayToString(value, 3);
                    }
                    infoData[item?.key] = `${pre}${value}${post}`;
                });
                const finalObj = {
                    style: {
                        marginTop: 10,
                        background: promoMappedObj?.color,
                        marginLeft: startDatePercentage,
                        width: `calc(${durationInPercentage}% + 0.5px)`,
                    },
                    infoData,
                };
                outputObj[weekString] = [finalObj];
            }
        }

        result.push(outputObj);
    });
    return { result, calenderColumns, nonCalenderColumns, hoverData, legendObj, deletedCountParser, statues };
}
const calculatePercentage = (date: string, outputObj: any) => {
    const dateObj = new Date(date);

    // Get the week the date falls into
    const weeks = Object.keys(outputObj).map((week) => new Date(week));
    let relevantWeekStart = null;

    for (let i = 0; i < weeks.length - 1; i++) {
        if (dateObj >= weeks[i] && dateObj < weeks[i + 1]) {
            relevantWeekStart = weeks[i];
            break;
        }
    }
    const weekString = dayjs(relevantWeekStart).format('MM/DD/YY');

    // Calculate percentage
    const daysFromStart = calculateDifferenceInDays(dateObj, relevantWeekStart);
    return { percentage: ((daysFromStart / 28) * 100).toFixed(2) + '%', weekString };
};

//calculates difference between 2 dates
function calculateDifferenceInDays(endDate: Dayjs | Date | null, startDate: Dayjs | Date | null) {
    const difference = dayjs(endDate).startOf('day').diff(dayjs(startDate).startOf('day'), 'day');
    return difference;
}

function fetchTypeProperties(type: eventStatusType) {
    switch (type) {
        case 'updated':
            return {
                component: (
                    <>
                        <EditOutlined style={{ color: colorConstants.blueAlt5 }} />
                    </>
                ),
                label: 'Edited',
                //  backgroundColor: colorConstants.blueAlt4,
                backgroundColor: '!bg-violetAlt2',
            };
        case 'added':
            return {
                component: (
                    <>
                        <PlusSquareOutlined style={{ color: colorConstants.violetAlt }} />
                    </>
                ),
                label: 'Added',
                //  backgroundColor: colorConstants.grey,
                backgroundColor: '!bg-bgBackground',
            };
        case 'deleted':
            return {
                component: (
                    <>
                        <DeleteOutlined style={{ color: colorConstants.greyAlt14 }} />
                    </>
                ),
                label: 'Deleted',
                //   backgroundColor: colorConstants.greyAlt9,
                backgroundColor: '!bg-backgroundGreyAlt',
            };
        case 'in_sync':
            return {
                component: (
                    <>
                        <DownloadOutlined style={{ color: colorConstants.greyAlt4 }} />
                    </>
                ),
                label: 'Imported',
                //backgroundColor: '!bg-greyBackgroundAlt',
                backgroundColor: '!bg-white',
            };
        default:
            return {
                component: <></>,
                label: '',
                backgroundColor: '!bg-white',
            };
    }
}

export function parseKpiTableData(
    data: any[],
    kpiConfigData: KpiConfigType | null,
    groupBy: string[],
    groupByMapping: Record<string, any>,
    eventViewType: EventViewType,
) {
    try {
        const kpiColumns = data?.[0]?.kpis?.map((kpi: any) => {
            const key = kpi?.value;
            let label = '';
            let sortKey = false;
            if (kpiConfigData && key in kpiConfigData) {
                label =
                    eventViewType === 'promo_metrics' ? kpiConfigData?.[key]?.label : kpiConfigData?.[key]?.planLabel;
                sortKey = false;
            }
            return {
                label,
                key,
                sortKey,
            };
        });

        const nonKpiColumns = groupBy?.map((val: string) => {
            return {
                key: groupByMapping?.[val]?.dataKey,
                label: groupByMapping?.[val]?.label,
                sortKey: groupByMapping?.[val]?.sortKey,
            };
        });
        const result = data?.map((item: any, index: number) => {
            let itemObj: Record<string, any> = {};
            let dynamicKey = '';
            //non kpi cols
            nonKpiColumns?.forEach((nonKpi: any) => {
                //show id along with name for these columns
                if (['event_name', 'promo_name', 'plan_name'].includes(nonKpi?.key)) {
                    const idKey = nonKpi?.key?.replace('name', 'id');
                    itemObj[nonKpi?.key] = item?.[idKey] + ' - ' + item?.[nonKpi?.key];
                } else itemObj[nonKpi?.key] = item?.[nonKpi?.key];
                dynamicKey += item?.[nonKpi?.key];
            });
            itemObj['key'] = dynamicKey + index;
            // kpi cols
            kpiColumns?.forEach((kpi: any) => {
                let pre = '';
                let post = '';
                let allowedDecimals = 0;
                const kpiItem = item?.kpis?.find((val: any) => {
                    return val?.value === kpi?.key;
                });
                const value1 = kpiItem?.value1;
                const value2 = kpiItem?.value2;
                const absChange = _.isNil(value2) ? null : value1 - value2;
                const percentageChange = _.isNil(value2) ? undefined : getPercentageChangeV2(value1, value2);
                if (kpiConfigData && kpi?.key in kpiConfigData) {
                    pre = kpiConfigData?.[kpi?.key]?.pre;
                    post = kpiConfigData?.[kpi?.key]?.post;
                    allowedDecimals = kpiConfigData?.[kpi?.key]?.allowedDecimals;
                }
                itemObj[kpi?.key] = { pre, post, allowedDecimals, value1, value2, absChange, percentageChange };
            });
            return itemObj;
        });
        return { kpiColumns, nonKpiColumns, result };
    } catch (error) {
        throw error;
    }
}

export function parseEventsForEventLibrary(data: any[], dimensionConfigData: DimensionConfigType | null) {
    try {
        const result = data?.map((item: any) => {
            return {
                planId: item?.plan_id,
                key: item?.plan_id,
                plan: item?.plan_name,
                isViewMode: false,
                plan_year: item?.plan_year,
                [dimensionConfigData?.dim1Attribute?.key ?? '']: item?.dim1_attribute_value?.join(', '),
                [dimensionConfigData?.dim2Attribute?.key ?? '']: item?.dim2_attribute_value,
            };
        });
        return result;
    } catch (error) {
        throw error;
    }
}

export function parseEventsForImportEventLibrary(data: any[]) {
    try {
        const result = data?.map((item: any) => {
            return {
                eventId: item?.event,
                key: item?.event + item?.promo_id,
                event: item?.event_name,
                promo: item?.promo_name,
                promoId: item?.promo_id,
                isViewMode: false,
            };
        });
        return result;
    } catch (error) {
        throw error;
    }
}

export function parsePlansForImport(data: any[]) {
    try {
        const result = data?.map((item: any) => {
            return {
                label: item?.plan_name,
                value: item?.plan_id,
            };
        });
        return result;
    } catch (error) {
        throw error;
    }
}

export function parseEventsByPlanIdForImport(data: any[], promoConfigDictionary: PromoConfigDictionary) {
    try {
        const result = data?.map((item: any) => {
            return {
                eventId: item?.event,
                key: item?.event + item?.promo_id,
                event: item?.event_name,
                promo: item?.promo_name,
                promoId: item?.promo_id,
                tacticType: promoConfigDictionary.tactics[item?.tactic_type],
                promoStartDateUnix: item?.promo_start_date,
                promoEndDateUnix: item?.promo_end_date,
                promoStartDate: formatDatePreservingTimeValues(item?.promo_start_date),
                promoEndDate: formatDatePreservingTimeValues(item?.promo_end_date),
                promoTypeId: item?.promo_type,
                promoType: promoConfigDictionary.promotions[item?.promo_type],
                isViewMode: false,
            };
        });
        return result;
    } catch (error) {
        throw error;
    }
}

export const getPromoConfigDataParser = (data: any[]) => {
    const result: PromoConfigDictionary = {
        promotions: {},
        promoMetrics: {},
        spendMetrics: {},
        tactics: {},
        kpiDependencies: {},
        kpiFormulas: {},
        promoMetricMapping: {},
    };

    data.forEach((promotion) => {
        // Populate promotions
        result.promotions[promotion.promotion_id] = promotion.promotion_name;

        // Populate tactics
        promotion.tactics.forEach((tactic: any) => {
            result.tactics[tactic.tactic_id] = tactic.tactic_name;
        });
        let promoMetrics = {};
        let spendMetrics = {};
        // Populate promoMetrics with editable property
        promotion.promo_metrics.forEach((metric: any) => {
            const promoMetric = {
                id: metric.promo_metric_id,
                name: metric.promo_metric_name,
                name_key: metric?.promo_metric_name_key,
                editable: metric.editable,
                defaultShow: metric.default_show_on_expand,
                allowedDecimals: metric.allowed_decimals,
                allowNegative: metric.allow_negative,
                preUnit: metric.pre_unit,
                postUnit: metric.post_unit,
                minValue: metric.min_value,
                maxValue: metric.max_value,
                orderId: metric.order_id,
                groupName: metric.group_name,
                isLumpsum: metric?.is_lumpsum,
                customerType: metric?.customer_type,
                is_past_dates_editable: metric?.is_past_dates_editable ?? false,
            };
            result.promoMetrics[metric.promo_metric_id] = promoMetric;
            result.kpiDependencies[metric.promo_metric_name_key] = metric.dependencies;
            result.kpiFormulas[metric.promo_metric_name_key] = metric.formulas;
            promoMetrics = { ...promoMetrics, [metric.promo_metric_id]: promoMetric };
        });

        // Populate spendMetrics with editable property
        promotion.spend_metrics.forEach((metric: any) => {
            const spendMetric = {
                id: metric.spend_metric_id,
                name: metric.spend_metric_name,
                name_key: metric?.spend_metric_name_key,
                editable: metric.editable,
                defaultShow: metric.default_show_on_expand,
                allowedDecimals: metric.allowed_decimals,
                allowNegative: metric.allow_negative,
                preUnit: metric.pre_unit,
                postUnit: metric.post_unit,
                minValue: metric.min_value,
                maxValue: metric.max_value,
                orderId: metric.order_id,
                groupName: metric.group_name,
                isLumpsum: metric?.is_lumpsum,
                customerType: metric?.customer_type,
                is_past_dates_editable: metric?.is_past_dates_editable ?? false,
            };
            result.spendMetrics[metric.spend_metric_id] = spendMetric;
            result.kpiDependencies[metric.spend_metric_name_key] = metric.dependencies;
            result.kpiFormulas[metric.spend_metric_name_key] = metric.formulas;
            spendMetrics = { ...spendMetrics, [metric.spend_metric_id]: spendMetric };
        });
        result.promoMetricMapping[promotion.promotion_id] = {
            name: promotion.promotion_name,
            spendMetrics,
            promoMetrics,
        };
    });

    return result;
};
